'use client';

import base, { AxiosInstance } from 'axios';
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';
import { AuthContext, clearStorage } from './Authentication';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
} from '@mui/material';
import { Iconify } from '@/components/common/iconify';
import { UI_ICONS } from '@/lib/ui-icons';
import { getMailhouseApiOrigin, getSelfOriginForRedirects } from '@/config/apiConfig';
import { extractApiMessage } from './Axios';
import { getMyApiToken } from '@/services/myApiToken';

/**
 * Mailhouse Management axios provider — Boostt-INTERNAL-STAFF-ONLY tooling for the
 * mailhouse-svc microservice (partner XMPie config, partner access requests,
 * production endpoints, template versions). Deliberately much simpler than
 * `DirectMailAxios.tsx`: Mailhouse has no sub-tenant/RBAC member-scoping concept
 * (no `member_user_ids`, no aggregate account scoping), so none of that machinery
 * is replicated here.
 *
 * ---------------------------------------------------------------------------
 * AUTH — READ BEFORE TOUCHING THIS FILE
 * ---------------------------------------------------------------------------
 * mailhouse-svc runs on a raw Lambda Function URL, not a `*.boostt.ai` subdomain,
 * so a cookie scoped to `Domain=.boostt.ai` is never sent to this host by the
 * browser — cookie-based auth does not work here at all. `SessionAuthMiddleware`
 * (`mailhouse-svc/src/presentation/middleware/session_auth.py`) accepts exactly
 * two credential types, and there is no cookie mechanism anywhere in that service:
 *   1. X-Master-Token — service-to-service calls between Boostt backends. NEVER
 *      put this in a client-side bundle; it's a shared backend secret.
 *   2. `Authorization: Bearer <token>` + `X-User-Id: <id>` — a logged-in Boostt
 *      staff member's OWN per-user API token, validated the same way
 *      audienceBuilder/notification-svc/direct_mail validate theirs.
 *
 * This is a browser-facing admin UI, so it uses path (2). The staff member's
 * token is obtained via `getMyApiToken()` (`@/services/myApiToken.ts`), which
 * calls user-login-svc's `GET /me/token` using the NORMAL cross-domain Boostt
 * session cookie (that call — and only that call — still uses cookies, because
 * user-login-svc *is* on `*.boostt.ai` and still does cookie auth). The request
 * interceptor below fetches that token (cached in memory by `myApiToken.ts`) and
 * attaches it to every outgoing mailhouse-svc request. If the token fetch fails
 * (no session, no token provisioned yet, etc.) the request still goes out, just
 * without the auth headers, so it comes back as a normal 401. That 401 is told
 * apart from a genuinely expired Boostt session (a distinct "Mailhouse Access Not
 * Set Up" dialog vs. "Session Expired") via a marker set on the request when the
 * token fetch itself failed — see the `__mailhouseTokenFetchFailed` flag below.
 *
 * `withCredentials: true` is kept on the mailhouse-svc client below only because
 * it's harmless (mailhouse-svc ignores any cookie it receives); it is NOT part of
 * the auth story for this file anymore.
 *
 * Nav-level + page-level RBAC gating (permission `mailhouse.manage`) controls who
 * can reach this UI at all; this axios instance does not re-implement that.
 */

export const MailhouseAxiosContext = createContext<AxiosInstance | null>(null);

export const isCancel = base.isCancel;

function getMailhouseInstance(
  auth: { isAuthenticated?: boolean },
  setSessionExpiredOpen: (open: boolean) => void,
  setTokenMissingOpen: (open: boolean) => void
): AxiosInstance {
  const axios = base.create({
    baseURL: getMailhouseApiOrigin(),
    withCredentials: true,
  });

  axios.interceptors.request.use(async (req) => {
    req.headers['Accept'] = 'application/json';
    req.headers['X-Frontend-Page'] = typeof window !== 'undefined' ? window.location.href : '';
    try {
      const { token, user_id } = await getMyApiToken();
      req.headers['Authorization'] = `Bearer ${token}`;
      req.headers['X-User-Id'] = String(user_id);
    } catch {
      // No token available (not logged in, or none provisioned yet). Let the
      // request go out anyway — mailhouse-svc will return a clean 401, which
      // the response interceptor below surfaces exactly like any other error.
      // Mark the request so that 401 can be told apart from a genuinely expired
      // session below — we can't tell from the frontend alone *why* the token
      // fetch failed, but we can tell that it did.
      (req as any).__mailhouseTokenFetchFailed = true;
    }
    return req;
  });

  axios.interceptors.response.use(
    (res) => res,
    (err) => {
      if (typeof window !== 'undefined' && window.navigator?.onLine && err.response) {
        if (err.response.status === 401) {
          if (err.config?.__mailhouseTokenFetchFailed) {
            setTokenMissingOpen(true);
          } else if (auth?.isAuthenticated) {
            location.href = `${getSelfOriginForRedirects()}/v2/login`;
          } else {
            setSessionExpiredOpen(true);
          }
        } else {
          const status = err.response.status;
          const apiMessage = extractApiMessage(err.response?.data);
          const isProd = process.env.NODE_ENV === 'production';
          err.errorCode = status;
          err.formattedError =
            (isProd && status >= 500 ? null : apiMessage) || `Request failed with status ${status}`;
        }
      } else {
        err.formattedError = 'An error occurred while connecting to the network';
      }
      return Promise.reject(err);
    }
  );

  return axios;
}

export default function MailhouseAxios({ children }: { children: ReactNode }) {
  const authData = useContext(AuthContext);
  const auth = authData || {};
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [tokenMissingOpen, setTokenMissingOpen] = useState(false);

  const client = useMemo(
    () => getMailhouseInstance(auth, setSessionExpiredOpen, setTokenMissingOpen),
    [authData] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleSessionExpiredClose = () => {
    setSessionExpiredOpen(false);
    clearStorage();
    if (typeof window !== 'undefined') {
      location.href = `${getSelfOriginForRedirects()}/v2/login`;
    }
  };

  return (
    <>
      <MailhouseAxiosContext.Provider value={client}>{children}</MailhouseAxiosContext.Provider>

      <Dialog open={tokenMissingOpen} onClose={() => setTokenMissingOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify icon={UI_ICONS.warning} sx={{ color: 'warning.main' }} />
            <Typography variant="h6" component="div">
              Mailhouse Access Not Set Up
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            You&apos;re logged in, but don&apos;t have a Mailhouse API token provisioned yet.
            Logging in again won&apos;t fix this — contact an administrator to request access.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTokenMissingOpen(false)} variant="contained" color="primary" fullWidth>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={sessionExpiredOpen} onClose={handleSessionExpiredClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Iconify icon={UI_ICONS.warning} sx={{ color: 'warning.main' }} />
            <Typography variant="h6" component="div">
              Session Expired
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Your session has expired. Please log in again to continue.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSessionExpiredClose} variant="contained" color="primary" fullWidth>
            Go to Login
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
